import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Typography,
  TextField,
} from '@mui/material';
import { RaRecord } from 'ra-core';
import { Link as RRLink } from 'react-router-dom';
import { useNotify, useRefresh } from 'react-admin';
import getAxios from '../../../getAxios';
import ButtonProgress from 'components/ButtonProgress';

const axios = getAxios();

type RejectGeneratedRateModalProps = {
  record: RaRecord;
  isOpen: boolean;
  onClose: () => void;
};

export const RejectGeneratedRateModal = ({
  record,
  isOpen,
  onClose,
}: RejectGeneratedRateModalProps) => {
  const [comment, setComment] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const notify = useNotify();
  const refresh = useRefresh();

  const positionName = record.position?.display_name || 'Unknown Position';
  const marketName = record.market?.display_name || 'Unknown Market';
  const marketId = record.market?.id || '';

  const handleReject = async () => {
    try {
      setIsLoading(true);
      await axios.post(`/v3/admin/auto_generated_rates/${record.id}/reject`, { comment });
      notify('The rate was rejected', { type: 'success' });
      refresh();
      onClose();
    } catch {
      notify('Error: Rate not rejected', { type: 'error' });
    } finally {
      setIsLoading(false);
      setComment('');
    }
  };

  return (
    <Dialog open={isOpen} maxWidth="xs">
      <DialogTitle>Confirm Rejection</DialogTitle>
      <DialogContent>
        <Box sx={{ marginTop: 2 }}>
          <Typography variant="body1">
            You are about to <strong>Reject</strong> the generated rate for{' '}
            <strong>
              {positionName} in{' '}
              <Link
                component={RRLink}
                to={`/market/${marketId}/show`}
                onClick={(e) => e.stopPropagation()}
                target="_blank"
              >
                {marketName}
              </Link>
            </strong>
          </Typography>
        </Box>
        <TextField
          variant="outlined"
          label="Rejection Comment (Optional)"
          multiline
          rows={4}
          fullWidth
          sx={{ marginY: 2 }}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <Typography variant="body1">
          This will reject the generated rate. <strong>Are you sure you want to continue?</strong>
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonProgress
          onClick={handleReject}
          color="error"
          variant="contained"
          loading={isLoading}
        >
          Reject
        </ButtonProgress>
      </DialogActions>
    </Dialog>
  );
};
